// src/components/dashboards/controlStyle.ts
//
// The style of a dashboard's control blocks (plan 2026-10-19-control-block-
// styling, D2-D4; ADR-0480). Ten attributes: per state a background, border
// and text colour, and a shared border width, radius, size and spacing.
//
// Two levels, both OPTIONAL per attribute: the dashboard's own
// (`DashboardDTO.control_style`) and the block's own keys in its stored
// `control` dict. There is NO platform value: an unset attribute emits no
// CSS variable, so each display keeps its own look of today through the
// `var(--ctl-*, <today>)` fallback in its inline style. The dashboard's
// variables sit on the canvas root, a block's on its tile wrapper, where
// they win.
//
// A stored value is UNTRUSTED (a seed or a copy writes specs without the
// backend's `open_control_shape`), so `coerceControlStyle` re-checks every
// key against its closed set and a colour resolves by map LOOKUP or a checked
// hex (`dashboardDrawingColor`'s rules); nothing is pasted into a style
// unchecked. A value that fails is left out and reported, so a browser check
// can see it (`data-control-style-invalid`).
import type { CSSProperties } from "react";
import { isCustomColor, paletteSlotIndex } from "@verity/report-components";
import type { DashboardTheme } from "./DashboardThemeContext";

/** The colour attributes, in the order the editor and the MCP list them. */
export const CONTROL_COLOR_KEYS = [
  "selected_bg",
  "selected_border",
  "selected_text",
  "unselected_bg",
  "unselected_border",
  "unselected_text",
] as const;
export type ControlColorKey = (typeof CONTROL_COLOR_KEYS)[number];

/** The closed sets of the other four (`api/constants/control_styles.py`). */
export const CONTROL_BORDER_WIDTHS = [0, 1, 2, 3] as const;
/** px; 999 is a pill. */
export const CONTROL_RADII = [0, 2, 4, 6, 8, 12, 999] as const;
export const CONTROL_SIZES = ["xs", "sm", "md"] as const;
/** px between values. */
export const CONTROL_SPACINGS = [2, 4, 6, 8, 12] as const;

export type ControlSize = (typeof CONTROL_SIZES)[number];
export const CONTROL_STYLE_KEYS = [...CONTROL_COLOR_KEYS, "border_width", "radius", "size", "spacing"] as const;
export type ControlStyleKey = (typeof CONTROL_STYLE_KEYS)[number];

/** A control colour: a token, a theme slot, or a lowercase hex. */
export const CONTROL_COLOR_TOKENS = ["rule", "muted", "text", "primary", "primary_light"] as const;

export type ControlStyle = Partial<{
  selected_bg: string;
  selected_border: string;
  selected_text: string;
  unselected_bg: string;
  unselected_border: string;
  unselected_text: string;
  border_width: (typeof CONTROL_BORDER_WIDTHS)[number];
  radius: (typeof CONTROL_RADII)[number];
  size: ControlSize;
  spacing: (typeof CONTROL_SPACINGS)[number];
}>;

const CONTROL_TOKEN_CSS: Readonly<Record<(typeof CONTROL_COLOR_TOKENS)[number], string>> = {
  rule: "var(--mantine-color-default-border)",
  muted: "var(--mantine-color-dimmed)",
  text: "var(--mantine-color-text)",
  primary: "var(--mantine-primary-color-filled)",
  primary_light: "var(--mantine-primary-color-light)",
};

function own<T extends string>(map: Readonly<Record<T, string>>, value: unknown): value is T {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(map, value);
}

/** Whether `value` is a control colour in its stored spelling. */
export function isControlColor(value: unknown): value is string {
  return own(CONTROL_TOKEN_CSS, value) || paletteSlotIndex(value) !== null || isCustomColor(value);
}

function inSet<T extends string | number>(set: readonly T[], value: unknown): value is T {
  // `typeof` first: `true` and "4" never pass as a number.
  return (typeof value === typeof set[0]) && (set as readonly unknown[]).includes(value);
}

/** The checked style of a stored dict (a block's `control`, the dashboard's
 *  `control_style`): only the keys that are set AND inside their set, plus
 *  the keys that were set to something else. Null / absent = unset. */
export function coerceControlStyle(raw: unknown): { style: ControlStyle; invalid: ControlStyleKey[] } {
  const style: ControlStyle = {};
  const invalid: ControlStyleKey[] = [];
  const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  for (const key of CONTROL_COLOR_KEYS) {
    const v = r[key];
    if (v === undefined || v === null) continue;
    if (isControlColor(v)) style[key] = v;
    else invalid.push(key);
  }
  const sets = {
    border_width: CONTROL_BORDER_WIDTHS,
    radius: CONTROL_RADII,
    size: CONTROL_SIZES,
    spacing: CONTROL_SPACINGS,
  } as const;
  for (const key of Object.keys(sets) as (keyof typeof sets)[]) {
    const v = r[key];
    if (v === undefined || v === null) continue;
    if (inSet(sets[key] as readonly (string | number)[], v)) (style as Record<string, unknown>)[key] = v;
    else invalid.push(key);
  }
  return { style, invalid };
}

/** The CSS colour of a checked control colour: a token by lookup, a slot
 *  by the theme, a hex as itself; null when a slot has no theme colour (the
 *  display then draws today's colour). A slot or hex does not flip in dark
 *  mode: the three colours of a state are one matched set (D4). */
export function controlColorCss(value: string, theme: DashboardTheme): string | null {
  if (own(CONTROL_TOKEN_CSS, value)) return CONTROL_TOKEN_CSS[value];
  const index = paletteSlotIndex(value);
  if (index !== null) {
    const hex = theme?.[index];
    return isCustomColor(hex) ? hex : null;
  }
  return isCustomColor(value) ? value : null;
}

/** Mantine's control heights and side padding per size (`xs` is what a value
 *  button draws today). */
export const CONTROL_SIZE_PX: Readonly<Record<ControlSize, { height: number; paddingX: number }>> = {
  xs: { height: 30, paddingX: 14 },
  sm: { height: 36, paddingX: 18 },
  md: { height: 42, paddingX: 22 },
};

/** The CSS variable of each attribute (read by OpenControlTile, its module
 *  CSS and TimeRangeSelector, always with today's value as the fallback). */
export const CONTROL_VARS = {
  selected_bg: "--ctl-sel-bg",
  selected_border: "--ctl-sel-bd",
  selected_text: "--ctl-sel-color",
  unselected_bg: "--ctl-bg",
  unselected_border: "--ctl-bd",
  unselected_text: "--ctl-color",
  border_width: "--ctl-bd-width",
  radius: "--ctl-radius",
  spacing: "--ctl-spacing",
  /** Derived from `size`. */
  height: "--ctl-height",
  paddingX: "--ctl-pad-x",
  /** Derived from a set bg: Mantine's own hover stays when no bg is set. */
  selected_hover: "--ctl-sel-hover",
  unselected_hover: "--ctl-hover",
} as const;

const hover = (bg: string) => `color-mix(in srgb, ${bg} 90%, var(--mantine-color-text))`;

/** The CSS variables of a checked style: ONLY the attributes that are set. */
export function controlStyleVars(style: ControlStyle, theme: DashboardTheme): CSSProperties {
  const out: Record<string, string> = {};
  for (const key of CONTROL_COLOR_KEYS) {
    const v = style[key];
    if (v === undefined) continue;
    const css = controlColorCss(v, theme);
    if (css === null) continue;
    out[CONTROL_VARS[key]] = css;
  }
  if (out[CONTROL_VARS.selected_bg]) out[CONTROL_VARS.selected_hover] = hover(out[CONTROL_VARS.selected_bg]);
  if (out[CONTROL_VARS.unselected_bg]) out[CONTROL_VARS.unselected_hover] = hover(out[CONTROL_VARS.unselected_bg]);
  if (style.border_width !== undefined) out[CONTROL_VARS.border_width] = `${style.border_width}px`;
  if (style.radius !== undefined) out[CONTROL_VARS.radius] = `${style.radius}px`;
  if (style.spacing !== undefined) out[CONTROL_VARS.spacing] = `${style.spacing}px`;
  if (style.size !== undefined) {
    out[CONTROL_VARS.height] = `${CONTROL_SIZE_PX[style.size].height}px`;
    out[CONTROL_VARS.paddingX] = `${CONTROL_SIZE_PX[style.size].paddingX}px`;
  }
  return out as CSSProperties;
}

/** `var(--ctl-x, <today>)`: what every display writes in place of a fixed value. */
export function ctlVar(name: string, today: string): string {
  return `var(${name}, ${today})`;
}
